import { A } from '@ember/array';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import { task } from 'ember-concurrency';
import { type Task } from 'ember-concurrency';
import link from 'ember-link/helpers/link';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { eq, not, or } from 'ember-truth-helpers';
import type { GetCanReviewOrReadBlocklistMatchModel } from 'garaje/abilities/security';
import FeedItemFlaggedScreener from 'garaje/components/approvals/feed-item-flagged-screener';
import NoReviewPermissionTooltip from 'garaje/components/approvals/no-review-permission-tooltip';
import WorkflowDecisionModal from 'garaje/components/approvals/workflow-decision-modal';
import MonogramOrPhoto from 'garaje/components/monogram-or-photo';
import arrayToSentence from 'garaje/helpers/array-to-sentence';
import removedString from 'garaje/helpers/removed-string';
import type { ReportSource } from 'garaje/models/approval-status';
import type { EntryWithVisitApproval } from 'garaje/models/entry';
import type ScreenerResult from 'garaje/models/screener-result';
import type { BlocklistReport } from 'garaje/models/screener-result';
import type UserModel from 'garaje/models/user';
import italicizeIfRemoved from 'garaje/modifiers/italicize-if-removed';
import type AjaxService from 'garaje/services/ajax';
import type CurrentLocationService from 'garaje/services/current-location';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import type { WorkflowTasksPermissionsResponse } from 'garaje/types/workflow-approvals';
import {
  deriveCanApproveWorkflow,
  deriveUserIsAssignedApproverWithoutPermission,
} from 'garaje/utils/derive-can-approve-workflow';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { viewportProperties } from 'garaje/utils/form-factor';
import { getScreenerDisplayName } from 'garaje/utils/get-screener-display-name';
import { notifyWorkflow } from 'garaje/utils/notify-workflow';
import { reads } from 'macro-decorators';

type Context = 'location' | 'property';

interface EntryFeedItemFlaggedSignature {
  Args: {
    approveEntryTask: Task<unknown, [EntryWithVisitApproval, screeners?: ReportSource[]]>;
    blocklistContacts: UserModel[];
    context?: 'location' | 'property';
    denyEntryTask: Task<unknown, [EntryWithVisitApproval]>;
    entry: EntryWithVisitApproval;
    idScanContacts: UserModel[];
    /**
     * Optional callback to mark entry as reviewed after workflow approval/denial
     */
    onEntryReviewed?: (entry: EntryWithVisitApproval) => void;
  };
  Element: HTMLDivElement;
}

export default class EntryFeedItemFlagged extends Component<EntryFeedItemFlaggedSignature> {
  @service declare abilities: AbilitiesService;
  @service declare ajax: AjaxService;
  @service declare currentLocation: CurrentLocationService;
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;
  @service declare state: StateService;

  @reads('state.features.canAccessMultiPartApprovals') canAccessMultiPartApprovals!: boolean;

  @tracked workflowPermissionsChecked = false;
  @tracked canApproveWorkflow = true;
  @tracked userIsAssignedApproverWithoutPermission = false;
  @tracked showWorkflowDecisionModal = false;
  @tracked workflowDecisionAction: 'approve' | 'deny' | null = null;

  constructor(owner: unknown, args: EntryFeedItemFlaggedSignature['Args']) {
    super(owner, args);

    if (this.workflowId) {
      void this.fetchWorkflowPermissionsTask.perform();
    } else {
      this.workflowPermissionsChecked = true;
    }
  }

  get workflowId(): string | null {
    const resultWithWorkflow = this.args.entry.visitApproval.screenerResults.find(
      (result) => result.workflowId !== null,
    );
    return resultWithWorkflow?.workflowId ?? null;
  }

  get isPartiallyApproving(): boolean {
    return this.args.entry.visitApproval.partiallyApprovable;
  }

  get unreviewableScreenerSources(): string {
    return arrayToSentence(
      this.args.entry.visitApproval.unreviewableScreenerSources.map(
        (source) => getScreenerDisplayName(source) ?? source,
      ),
    );
  }

  get approveCopy(): string {
    // has multiple approvals and can review all
    if (this.multipleApprovals && this.canReviewAllReviewableScreeners) return 'Approve all';
    return 'Approve';
  }

  get multipleApprovals(): boolean {
    return this.args.entry.visitApproval.reviewResults.length >= 2;
  }

  get blocklistApproval(): ScreenerResult<BlocklistReport> | undefined {
    const { entry } = this.args;

    return entry.visitApproval.blocklistReviewResult;
  }

  get idScanningApproval(): ScreenerResult | undefined {
    const { entry } = this.args;
    return entry.visitApproval.idScanningReviewResult;
  }

  get idScanningPendingApproval(): ScreenerResult | undefined {
    const { entry } = this.args;

    return entry.visitApproval.idScanningPendingResult;
  }

  get hasIdScanningPendingApproval(): boolean {
    return !!this.idScanningPendingApproval;
  }

  get canReview(): boolean {
    // if we are using multi part approvals, we should still be able to review the parts of the approval that we have access to
    if (this.canAccessMultiPartApprovals) return this.canReviewSomeReviewableScreeners;
    return this.canReviewAllReviewableScreeners;
  }

  get canReviewOrReadMatch(): boolean {
    // if we are using multi part approvals, we should still be able to review or read parts of the approval that we have access to
    if (this.canAccessMultiPartApprovals) {
      return this.canReviewSomeScreeners || this.canViewSomeScreeners;
    } else {
      return this.canReviewAllReviewableScreeners || this.canViewAllScreeners;
    }
  }

  /**
   * user can review at least one screener that is in a review state
   */
  get canReviewSomeReviewableScreeners(): boolean {
    return this.abilities.can('review some reviewable entry-approvals', this.#permissionsModel);
  }

  /**
   * user can review at least one screener regardless of the review status
   */
  get canReviewSomeScreeners(): boolean {
    return this.abilities.can('review some entry-approvals', this.#permissionsModel);
  }

  /**
   * user can view at least one screener regardless of the review status
   */
  get canViewSomeScreeners(): boolean {
    return this.abilities.can('view some entry-approvals', this.#permissionsModel);
  }

  /**
   * user can review all screeners in the approval of the review status
   * */
  get canReviewAllReviewableScreeners(): boolean {
    return this.abilities.can('review all reviewable entry-approvals', this.#permissionsModel);
  }

  /**
   * user can view all screeners in the approval
   * */
  get canViewAllScreeners(): boolean {
    return this.abilities.can('view entry-approvals', this.#permissionsModel);
  }

  get colspan(): number {
    let cols = 1;
    const dashboardFields = this.currentLocation.location.dashboardFields;

    if (dashboardFields) {
      cols += dashboardFields.length;
    }

    return cols;
  }

  get contactedNames(): string[] {
    let contacts: string[] = [];
    if (this.blocklistApproval && this.args.blocklistContacts) {
      contacts = [...contacts, ...this.args.blocklistContacts.map((contact) => contact.fullName)];
    }
    if (this.idScanningApproval && this.args.idScanContacts.map((contact) => contact.fullName)) {
      contacts = [...contacts, ...this.args.idScanContacts.map((contact) => contact.fullName)];
    }
    return A(contacts).uniq();
  }

  get context(): Context {
    return this.args.context ?? 'location';
  }

  fetchWorkflowPermissionsTask = task(async (): Promise<void> => {
    if (!this.workflowId) {
      this.workflowPermissionsChecked = true;
      return;
    }

    try {
      const response = await this.ajax.request<WorkflowTasksPermissionsResponse>(
        `/a/visits/api/v1/workflows/${this.workflowId}/tasks/permissions`,
      );

      this.canApproveWorkflow = deriveCanApproveWorkflow(response);
      this.userIsAssignedApproverWithoutPermission = deriveUserIsAssignedApproverWithoutPermission(response);
    } catch (_error) {
      this.canApproveWorkflow = false;
      this.userIsAssignedApproverWithoutPermission = false;
    } finally {
      this.workflowPermissionsChecked = true;
    }
  });

  approveEntryTask = task(
    waitFor(async (screeners?: ReportSource[], reason?: string) => {
      // This component-local version of the task exists so we can get instance-specific state.
      // The actual logic lives at a higher level so that, when invoked with `.unlinked()`, it all
      // executes; otherwise, once the approved entry was reloaded (either with an `entry.reload()`
      // call or by the realtime subscription that gets notified of changes to entries and pushes them
      // into the store out-of-band), this component gets torn down and the task is aborted before,
      // e.g., the flash message is shown. By keeping that logic out of this component, we don't
      // have to worry about that.
      // The instance-local state means that we can disable this entry's Approve/Deny buttons when
      // approveEntryTask or denyEntryTask is running without having to track state ourselves at
      // the higher level, or ended up disabling the buttons for _all_ entries instead of this one.

      if (this.workflowId) {
        await notifyWorkflow(this.ajax, this.flashMessages, this.workflowId, true, reason);

        const nonWorkflowScreeners = this.args.entry.visitApproval?.reviewableScreenerSourcesWithoutWorkflow ?? [];
        if (nonWorkflowScreeners.length) {
          try {
            await this.args.entry.approve(nonWorkflowScreeners);
          } catch (error) {
            this.flashMessages.showAndHideFlash('error', 'Error approving entry', {
              details: parseErrorForDisplay(error),
            });
            return;
          }
        }

        this.flashMessages.showAndHideFlash('success', 'Visitor approved');
        this.metrics.trackEvent('Dashboard Entry - Reviewed', {
          action: 'approve',
          entry_id: this.args.entry.id,
          source: 'Visitor Log',
          workflow: true,
          ...viewportProperties(),
        });
        if (this.args.onEntryReviewed) {
          this.args.onEntryReviewed(this.args.entry);
        }
        return;
      }

      await this.args.approveEntryTask.unlinked().perform(this.args.entry, screeners);
    }),
  );

  denyEntryTask = task(
    waitFor(async (reason?: string) => {
      // This component-local version of the task exists so we can get instance-specific state.
      // The actual logic lives at a higher level so that, when invoked with `.unlinked()`, it all
      // executes; otherwise, once the denied entry was reloaded (either with an `entry.reload()`
      // call or by the realtime subscription that gets notified of changes to entries and pushes them
      // into the store out-of-band), this component gets torn down and the task is aborted before,
      // e.g., the flash message is shown. By keeping that logic out of this component, we don't
      // have to worry about that.
      // The instance-local state means that we can disable this entry's Approve/Deny buttons when
      // approveEntryTask or denyEntryTask is running without having to track state ourselves at
      // the higher level, or ended up disabling the buttons for _all_ entries instead of this one.

      if (this.workflowId) {
        await notifyWorkflow(this.ajax, this.flashMessages, this.workflowId, false, reason);

        const nonWorkflowScreeners = this.args.entry.visitApproval?.reviewableScreenerSourcesWithoutWorkflow ?? [];
        if (nonWorkflowScreeners.length) {
          try {
            await this.args.entry.deny(nonWorkflowScreeners);
          } catch (error) {
            this.flashMessages.showAndHideFlash('error', 'Error denying entry', {
              details: parseErrorForDisplay(error),
            });
            return;
          }
        }

        this.flashMessages.showAndHideFlash('success', 'Visitor denied');
        this.metrics.trackEvent('Dashboard Entry - Reviewed', {
          action: 'deny',
          entry_id: this.args.entry.id,
          source: 'Visitor Log',
          workflow: true,
          ...viewportProperties(),
        });
        if (this.args.onEntryReviewed) {
          this.args.onEntryReviewed(this.args.entry);
        }
        return;
      }

      await this.args.denyEntryTask.unlinked().perform(this.args.entry);
    }),
  );

  @action
  trackClickEntryDetail(entry_id: string): void {
    this.metrics.trackEvent('Entry Details Clicked', { entry_id });
  }

  @action
  openWorkflowDecisionModal(action: 'approve' | 'deny'): void {
    if (this.workflowId) {
      this.workflowDecisionAction = action;
      this.showWorkflowDecisionModal = true;
    }
  }

  @action
  closeWorkflowDecisionModal(): void {
    this.showWorkflowDecisionModal = false;
    this.workflowDecisionAction = null;
  }

  @action
  handleWorkflowDecisionConfirm(reason?: string): void {
    if (this.workflowDecisionAction === 'approve') {
      void this.approveEntryTask.perform(undefined, reason);
    } else if (this.workflowDecisionAction === 'deny') {
      void this.denyEntryTask.perform(reason);
    }
    this.closeWorkflowDecisionModal();
  }

  @action
  handleApproveClick(): void {
    if (this.workflowId) {
      this.openWorkflowDecisionModal('approve');
    } else {
      void this.approveEntryTask.perform(undefined);
    }
  }

  @action
  handleDenyClick(): void {
    if (this.workflowId) {
      this.openWorkflowDecisionModal('deny');
    } else {
      void this.denyEntryTask.perform(undefined);
    }
  }

  get #permissionsModel(): GetCanReviewOrReadBlocklistMatchModel {
    const { entry } = this.args;

    return {
      context: this.context,
      didFailBlocklistCheck: Boolean(entry.visitApproval.blocklistReviewResult),
      visitApproval: entry.visitApproval,
      parent: entry,
    };
  }

  <template>
    <div
      data-test-entry-feed-item-flagged={{@entry.id}}
      class="table-row {{if this.canReviewOrReadMatch 'bg-white' 'text-carbon-70 bg-carbon-5'}}"
      ...attributes
    >
      <div class="table-cell border-b align-middle pl-1" style="width: 22px">
        {{#if this.canReviewOrReadMatch}}
          <img
            src="/assets/images/caution-triangle.svg"
            alt="needs attention"
            class="align-middle relative"
            style="min-width: 1.25rem; width:1.25rem; height:1.25rem; right: -1.5em;"
          />
        {{/if}}
      </div>

      <MonogramOrPhoto
        @href={{link route="visitors.entry" model=@entry.id}}
        @name={{removedString @entry.fullName}}
        @devicePhoto={{@entry.thumbnails.small}}
        @passportPhoto={{@entry.passportAvatar}}
        data-private
      />

      <div class="table-cell border-b align-middle p-2 text-carbon-50 min-w-[150px]" data-test-entry-feed-item>
        <LinkTo
          @route="visitors.entry"
          @model={{@entry.id}}
          {{on "click" (fn this.trackClickEntryDetail @entry.id)}}
          class="text-base font-bold no-underline text-carbon-70 hover:text-red-50 trans-color"
          data-test-entry-full-name
          data-private
          {{italicizeIfRemoved @entry.fullName}}
        >
          {{removedString @entry.fullName}}
        </LinkTo>
        {{#if @entry.host}}
          <p data-test-entry-host-wrapper class="m-0">
            <strong>Host:</strong>
            <span data-test-entry-host data-private {{italicizeIfRemoved @entry.host}}>{{removedString
                @entry.host
              }}</span>
          </p>
        {{/if}}
      </div>

      {{#if this.canReviewOrReadMatch}}
        <td class="table-cell border-b p-2 align-middle" colspan={{this.colspan}}>
          <div class="flex justify-between align-middle text-carbon-70 pr-8 text-sm">
            <div style="max-width: 30rem">
              <ul class="list-none p-0 m-0 leading-normal text-carbon-50" data-test-log-approval-review-reports>
                <FeedItemFlaggedScreener
                  @multipleApprovals={{this.multipleApprovals}}
                  @model={{@entry}}
                  @visitApproval={{@entry.visitApproval}}
                  @canReview={{this.canReview}}
                  @canReviewOrRead={{this.canReviewOrReadMatch}}
                  @approveTask={{this.approveEntryTask}}
                  @denyTask={{this.denyEntryTask}}
                  @context={{this.context}}
                />
              </ul>

              <LinkTo
                @route="visitors.entry"
                @model={{@entry.id}}
                {{on "click" (fn this.trackClickEntryDetail @entry.id)}}
                class="block my-2 text-xs"
                data-test-log-view-details
              >
                View details →
              </LinkTo>
            </div>
            {{#if this.canReview}}
              <div class="flex justify-center flex-col">
                <div style="width: 20rem">
                  <button
                    data-test-approve-button
                    type="button"
                    class="btn btn--outline btn--small mr-4"
                    style="width: 8rem"
                    {{on "click" this.handleApproveClick}}
                    disabled={{or
                      this.approveEntryTask.isRunning
                      this.denyEntryTask.isRunning
                      (not this.workflowPermissionsChecked)
                      (not this.canApproveWorkflow)
                      this.hasIdScanningPendingApproval
                    }}
                  >
                    <span class="icon-check-green mr-1"></span>
                    {{this.approveCopy}}
                    {{#if this.isPartiallyApproving}}
                      {{! prettier-ignore }}
                      <EmberTooltip
                        @tooltipClass="!rounded-none !p-2 !border !border-carbon-20 !text-carbon-80 !bg-white text-shadow-none z-50 !max-w-xs [&_.ember-tooltip-arrow]:hidden"
                      >
                        Approving this entry will still require additional approvals for <strong>{{this.unreviewableScreenerSources}}</strong> from another admin
                      </EmberTooltip>
                    {{/if}}
                  </button>
                  <button
                    data-test-deny-button
                    type="button"
                    class="btn btn--outline btn--small ml-2"
                    style="width: 8rem"
                    {{on "click" this.handleDenyClick}}
                    disabled={{or
                      this.approveEntryTask.isRunning
                      this.denyEntryTask.isRunning
                      (not this.workflowPermissionsChecked)
                      (not this.canApproveWorkflow)
                      this.hasIdScanningPendingApproval
                    }}
                  >
                    <span class="icon-cross-red mr-1"></span>
                    {{if this.multipleApprovals "Deny all" "Deny"}}
                  </button>
                  {{#if this.idScanningPendingApproval}}
                    <EmberTooltip @side="top" @text="Please wait until the visitor's ID is done processing." />
                  {{/if}}
                  {{#if this.userIsAssignedApproverWithoutPermission}}
                    <NoReviewPermissionTooltip />
                  {{/if}}
                </div>
              </div>
            {{/if}}
          </div>
        </td>
      {{else}}
        <td class="px-2 border-b align-middle" colspan={{this.colspan}} data-test-security-pending>
          This visitor requires approval for entry.
          {{#if this.contactedNames}}
            <br />
            {{arrayToSentence this.contactedNames}}
            {{if (eq this.contactedNames.length 1) "has" "have"}}
            been notified.
          {{/if}}
        </td>
      {{/if}}
    </div>

    {{#if this.showWorkflowDecisionModal}}
      <WorkflowDecisionModal
        @isApproving={{eq this.workflowDecisionAction "approve"}}
        @onConfirm={{this.handleWorkflowDecisionConfirm}}
        @onClose={{this.closeWorkflowDecisionModal}}
      />
    {{/if}}
  </template>
}
